import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer } from 'recharts';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';

interface TierRow {
  name: string;
  value: number;
  color: string;
}

const TIER_COLORS: Record<string, string> = {
  tier1: '#10b981',
  tier2: '#3b82f6',
  tier3: '#f59e0b',
  unqualified: '#f43f5e',
};

const TIER_LABELS: Record<string, string> = {
  tier1: 'Tier 1',
  tier2: 'Tier 2',
  tier3: 'Tier 3',
  unqualified: 'Unqualified',
};

export function TierChart() {
  const [data, setData] = useState<TierRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.calls.analytics.query({ tenantId: user.tenantId })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((result: any) => {
        const tiers: Array<{ tier: string | null; count: number }> = result.tierDistribution ?? [];
        if (tiers.length === 0) { setLoading(false); return; }
        const total = tiers.reduce((s, t) => s + (t.count ?? 0), 0);
        setData(
          tiers.map(t => ({
            name: TIER_LABELS[t.tier ?? ''] ?? (t.tier ?? 'Unknown'),
            value: total > 0 ? Math.round(((t.count ?? 0) / total) * 100) : 0,
            color: TIER_COLORS[t.tier ?? ''] ?? '#64748b',
          })),
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
      <h3 className="text-sm font-semibold text-slate-200 mb-1">Tier Distribution</h3>
      <p className="text-xs text-slate-500 mb-4">Call volume by qualification tier</p>
      {loading ? (
        <div className="h-[220px] flex items-center justify-center">
          <div className="text-slate-600 text-sm animate-pulse">Loading…</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-[220px] flex items-center justify-center">
          <p className="text-slate-600 text-sm">No classification data yet.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data} layout="vertical" margin={{ left: 16, right: 32, top: 4, bottom: 4 }}>
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              tick={{ fill: '#64748b', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={80}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#e2e8f0', fontSize: '12px' }}
              formatter={(value: number) => [`${value}%`, 'Share']}
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
            />
            <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
